import {useTranslation} from "react-i18next";
import React, {ReactNode, useEffect, useRef, useState} from "react";
import {useSnackbar} from "@dain/mui-components";
import {Navigate, useNavigate, useParams} from "react-router-dom";
import {useMutation} from "@tanstack/react-query";
import {useClientLocator} from "../../../client/ApiClientLocator";
import {DASHBOARD} from "../../../AppRoutes";
import {CircularProgress, Stack, Typography} from "@mui/material";
import {SubmissionTranslKey} from "../SubmissionTranslKey";
import {CommonTranslKey} from "../../../common/CommonTranslKey";
import Page from "../../../common/page/Page";
import Button from "../../../common/button/Button";
import {
    DigitalActionAuthorizationInitResultDto,
    DigitalActionAuthorizationResult
} from "../../../client/submission/SubmissionAuthorizationApiClient";
import {
    authorizationRetry,
    AUTHORIZATION_RETRY_DELAY_MS,
    getResponseStatus,
    isAuthorizationServiceUnavailableError,
    isSubmissionNotReadyForAuthorizationError,
    shouldUseReinit
} from "./AuthorizationRetryPolicy";
import AuthorizationServiceUnavailableDialog from "./AuthorizationServiceUnavailableDialog";
import SubmissionNotReadyForAuthorizationDialog from "./SubmissionNotReadyForAuthorizationDialog";


export interface SubmissionAuthorizationInitPageProps {
}

export function SubmissionAuthorizationInitPage(props: Readonly<SubmissionAuthorizationInitPageProps>) {
    const {t} = useTranslation();
    const snackbar = useSnackbar();
    const { submissionAuthorizationClient } = useClientLocator();
    const navigate = useNavigate();

    const { submissionId } = useParams();

    const initialized = useRef(false);
    const attemptCount = useRef(0);

    const [redirectUri, setRedirectUri] = useState<string>();
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [showUnavailableDialog, setShowUnavailableDialog] = useState(false);
    const [showNotReadyDialog, setShowNotReadyDialog] = useState(false);

    const mutation = useMutation<DigitalActionAuthorizationInitResultDto | undefined>({
        mutationFn: () => {
            attemptCount.current += 1;
            return shouldUseReinit(attemptCount.current)
                ? submissionAuthorizationClient.reinitDigitalActionAuthorization(submissionId!)
                : submissionAuthorizationClient.initDigitalActionAuthorization(submissionId!);
        },
        retry: authorizationRetry,
        retryDelay: AUTHORIZATION_RETRY_DELAY_MS,
        onSuccess: (response) => {
            if (!response) {
                setRedirectUri(DASHBOARD);
            } else if (response.result === DigitalActionAuthorizationResult.SUCCESS && response.postInitRedirectUri) {
                window.location.href = response.postInitRedirectUri;
            } else if (response.result === DigitalActionAuthorizationResult.ERROR) {
                setShowErrorMessage(true);
            } else {
                setRedirectUri(DASHBOARD);
            }
        },
        onError: (err: unknown) => {
            if (isAuthorizationServiceUnavailableError(err)) {
                setShowUnavailableDialog(true);
            } else if (isSubmissionNotReadyForAuthorizationError(err)) {
                setShowNotReadyDialog(true);
            } else if (getResponseStatus(err) === 400) {
                setShowErrorMessage(true);
            } else {
                snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), err);
                setShowErrorMessage(true);
            }
        },
    });

    const {mutate} = mutation;

    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            if (submissionId) {
                mutate();
            } else {
                setRedirectUri(DASHBOARD);
            }
        }
    }, [submissionId, mutate]);

    if (redirectUri) {
        return (<Navigate to={redirectUri} replace />);
    }

    var content: ReactNode;
    if (showUnavailableDialog) {
        content = (
            <AuthorizationServiceUnavailableDialog open={true} onClose={() => navigate(DASHBOARD)} />
        );
    } else if (showNotReadyDialog) {
        content = (
            <SubmissionNotReadyForAuthorizationDialog open={true} onClose={() => navigate(DASHBOARD)} />
        );
    } else if (showErrorMessage) {
        content = (
            <Stack direction={"column"} margin={2} spacing={2} alignItems={"flex-start"}>
                <Typography variant={"body2"}>{t(SubmissionTranslKey.INITIALIZATION_OF_AUTHORIZATION_HAS_FAILED)}</Typography>
                <Button variant={"secondary"} onClick={() => navigate(DASHBOARD)}>
                    {t(SubmissionTranslKey.GO_TO_DASHBOARD)}
                </Button>
            </Stack>
        );
    } else {
        const retrying = mutation.failureCount > 0;
        content = (
            <Stack direction={"column"} margin={2} spacing={2} justifyItems={"center"} alignItems={"center"} >
                <CircularProgress size={80} />
                <Typography variant={"body2"}>
                    {t(retrying
                        ? SubmissionTranslKey.AUTHORIZATION_SERVICE_TEMPORARILY_UNAVAILABLE_RETRYING
                        : SubmissionTranslKey.INITIALIZATION_OF_AUTHORIZATION_IN_PROGRESS)}
                </Typography>
            </Stack>
        );
    }

    return (
        <Page title={t(SubmissionTranslKey.AUTHORIZATION)}>
            {content}
        </Page>
    );
}
